import React, { useEffect, useRef, useState } from "react";
import {
    IonContent,
    IonGrid,
    IonRow,
    IonCol,
    IonButton,
    IonSelect,
    IonSelectOption,
    useIonAlert,
    IonLoading,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonModal,
    IonFooter,
    IonIcon,
    IonButtons
} from "@ionic/react";
import {arrowBack, cameraOutline, trash} from "ionicons/icons";
import { actionToUpdateUserData } from "../apiHelpers/CommonAction";
import useStore from "../zustand/useStore";
import AvatarCropModal from "./AvatarCropModal";
import {Capacitor} from "@capacitor/core";

/**
 * PRO EDITOR (no extra libs):
 * - Upload image (jpg/png/webp)
 * - Adjust: fit (cover/contain), zoom, drag to position
 * - Crop to square on Save, then upload via actionToUploadProfileImage(FormData)
 * - Resulting URL is sent back to UpdateUserData
 */

const EditUserDetailComponent = ({ isOpen, onClose }) => {
    const [presentAlert] = useIonAlert();
    const { userAuthDetail } = useStore();
    const { userInfo } = userAuthDetail;

    const [form, setForm] = useState({
        name: "",
        email: "",
        mobile: "",
        designation: "ca_member",
        dynamicField: "",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const imgEl = useRef(null);
    const [fitMode, setFitMode] = useState("cover"); // 'cover' | 'contain'
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 }); // px offsets inside frame
    const frameSize = 320; // square crop size in px

    // === Avatar state ===
    const [avatarUrl, setAvatarUrl] = useState("");
    const [avatarCanvasBlob, setAvatarCanvasBlob] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [rawImage, setRawImage] = useState(null); // File or DataURL for editor

    useEffect(() => {
        setForm({
            name: userInfo?.name || "",
            email: userInfo?.email || "",
            mobile: userInfo?.phone_number || "",
            designation: userInfo?.designation || "ca_member",
            dynamicField: userInfo?.identity_id || "",
        });
        setAvatarUrl(userInfo?.profile_image_url || "");
    }, []);

    const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const validate = () => {
        const err = {};
        if (!form.name.trim()) err.name = "Name is required";
        if (!/^\S+@\S+\.\S+$/.test(form.email)) err.email = "Valid email required";
        if (!/^\d{10}$/.test(form.mobile)) err.mobile = "Enter 10 digit mobile number";
        if (!form.dynamicField.trim()) err.dynamicField = "This field is required";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    // In your component (handleSubmit)
    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            const fd = new FormData();

            // 1) append cropped avatar blob (from doCropAndUpload)
            if (avatarCanvasBlob) {
                fd.append("avatar", avatarCanvasBlob, `avatar_${Date.now()}.jpg`);
            }

            // 2) append your existing text fields
            Object.entries(form || {}).forEach(([k, v]) => fd.append(k, v ?? ""));

            // 3) (optional) user id if your API needs it outside session
            if (userInfo?.id) fd.append("user_id", String(userInfo.id));

            await actionToUpdateUserData(fd);

            setLoading(false);
            presentAlert({
                header: "Success",
                message: "Your details have been updated successfully.",
                buttons: [{ text: "OK" }],
            });
        } catch (error) {
            setLoading(false);
            presentAlert({
                header: "Update Failed",
                message: error?.message || "Something went wrong",
                buttons: ["OK"],
            });
        }
    };



    const getDynamicLabel = () => {
        switch (form.designation) {
            case "ca_member":
                return "ICAI Membership No";
            case "ca_student":
                return "ICAI Student Regn. No";
            case "other_professional":
                return "ICSI / ICMA / Bar Council Membership No";
            case "accountant_other":
                return "PAN No";
            default:
                return "";
        }
    };

    // ===== Image Editor Logic =====
    const onPickImage = (e) => {
        try {
            const files = e?.target?.files;
            if (!files || !files[0]) {
                return; // user cancelled
            }

            const file = files[0];

            // Basic type check
            if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
                presentAlert({
                    header: "Unsupported file",
                    message: "Please choose a PNG, JPG, or WEBP image.",
                    buttons: ["OK"],
                });
                return;
            }

            // Optional: basic size guard (e.g. 10 MB)
            const MAX_SIZE_BYTES = 10 * 1024 * 1024;
            if (file.size > MAX_SIZE_BYTES) {
                presentAlert({
                    header: "Image too large",
                    message: "Please choose an image smaller than 10 MB.",
                    buttons: ["OK"],
                });
                return;
            }

            const reader = new FileReader();

            reader.onerror = () => {
                presentAlert({
                    header: "Image Error",
                    message: "Failed to read the selected image. Please try again.",
                    buttons: ["OK"],
                });
            };

            reader.onload = () => {
                if (!reader.result) {
                    presentAlert({
                        header: "Image Error",
                        message: "Could not load the image. Please try again.",
                        buttons: ["OK"],
                    });
                    return;
                }

                setRawImage({ file, dataUrl: reader.result });
                setEditorOpen(true);
                setFitMode("cover");
                setZoom(1);
                setOffset({ x: 0, y: 0 });
            };

            reader.readAsDataURL(file);
        } catch (err) {
            console.error("onPickImage error:", err);
            presentAlert({
                header: "Unexpected Error",
                message: "Something went wrong while selecting the image.",
                buttons: ["OK"],
            });
        } finally {
            // Clear the input so picking the same image again still triggers onChange
            if (e?.target) {
                e.target.value = "";
            }
        }
    };


    const doCropAndUpload = async () => {
        const img = imgEl.current;
        if (!img) {
            presentAlert({
                header: "Image Error",
                message: "No image loaded to crop. Please select an image again.",
                buttons: ["OK"],
            });
            return;
        }

        try {
            const frameW = frameSize;
            const frameH = frameSize;

            const centeredX = parseFloat(img.dataset.centeredX || "0");
            const centeredY = parseFloat(img.dataset.centeredY || "0");
            const offX = parseFloat(img.dataset.offsetX || "0");
            const offY = parseFloat(img.dataset.offsetY || "0");
            const drawW = parseFloat(img.dataset.drawW || "0");
            const drawH = parseFloat(img.dataset.drawH || "0");

            const tx = centeredX + offX;
            const ty = centeredY + offY;

            const canvas = document.createElement("canvas");
            canvas.width = frameW;
            canvas.height = frameH;

            const ctx = canvas.getContext("2d");
            if (!ctx) {
                throw new Error("Unable to create canvas context");
            }

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.fillStyle = "#fff";
            ctx.fillRect(0, 0, frameW, frameH);
            ctx.drawImage(img, tx, ty, drawW, drawH);

            setAvatarUrl(canvas.toDataURL());
            setEditorOpen(false);

            canvas.toBlob((blob) => {
                if (!blob) return;
                setAvatarCanvasBlob(blob);
            }, "image/jpeg", 0.9);
        } catch (err) {
            console.error("doCropAndUpload error:", err);
            presentAlert({
                header: "Image Error",
                message: "Failed to crop the image. Please try again.",
                buttons: ["OK"],
            });
        }
    };


    const deleteExistingImage = () => {
        setAvatarCanvasBlob(null);
        setAvatarUrl("");
        // add flag so handleSubmit knows to send delete_avatar
        setForm((prev) => ({ ...prev, delete_avatar: "true" }));
    };

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onClose} initialBreakpoint={1} breakpoints={[0, 1]} handleBehavior="cycle">
            <IonHeader className="job_detail_sheet_header sheet_modal_header">
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton className={"close_job_post_button"} onClick={onClose}>
                            <IonIcon icon={arrowBack}/>
                        </IonButton>
                    </IonButtons>
                    <IonTitle>Profile</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding no_padding_top">
                {/* === PROFILE HEADER === */}
                <div className="profile-header add_image_in_profile_component">
                    <div className="avatar-wrap">
                        <div className="avatar-ring">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="Avatar" className="avatar-img" />
                            ) : (
                                <div className="avatar-placeholder">{form?.name?.[0]?.toUpperCase() || "U"}</div>
                            )}
                        </div>
                        {/*{(Capacitor.getPlatform() !== 'ios') ?*/}
                            <label className="avatar-upload-btn">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={onPickImage}
                                    hidden
                                />

                                <IonIcon icon={cameraOutline} />
                                <span>Change</span>
                            </label>
                        {/*    :''*/}
                        {/*}*/}
                        {/*{(Capacitor.getPlatform() !== 'ios') ?*/}
                            <>
                                {(avatarUrl) &&
                                    (
                                        <label onClick={()=>deleteExistingImage()} className="avatar-upload-btn">
                                            <IonIcon icon={trash} />
                                            <span>Delete</span>
                                        </label>
                                    )
                                }
                            </>
                        {/*//     :''*/}
                        {/*// }*/}
                    </div>
                </div>

                {/* === FORM === */}
                <IonGrid className="register-grid for-profile-edit">
                    <IonRow>
                        <IonCol size="12">
                            <div className="form-group">
                                <label className="form-label">Name</label>
                                <input className="form-input" value={form.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="Full Name" />
                                {errors.name && <p className="danger_error_text">{errors.name}</p>}
                            </div>
                        </IonCol>

                        <IonCol size="12">
                            <div className="form-group">
                                <label className="form-label">Email Address</label>
                                <input className="form-input" value={form.email} readOnly onChange={(e) => handleChange("email", e.target.value)} placeholder="Email" />
                                {errors.email && <p className="danger_error_text">{errors.email}</p>}
                            </div>
                        </IonCol>

                        <IonCol size="12">
                            <div className="form-group">
                                <label className="form-label">Mobile</label>
                                <input type="tel" className="form-input" readOnly value={form.mobile} onChange={(e) => handleChange("mobile", e.target.value)} placeholder="10-digit Mobile Number" />
                                {errors.mobile && <p className="danger_error_text">{errors.mobile}</p>}
                            </div>
                        </IonCol>

                        <IonCol size="12" sizeMd={6}>
                            <div className="form-group">
                                <label className="form-label">Designation</label>
                                <IonSelect className="form-input ion-slide-select" value={form.designation} onIonChange={(e) => handleChange("designation", e.detail.value)} interface="action-sheet" placeholder="Select your designation">
                                    <IonSelectOption value="ca_member">CA Member</IonSelectOption>
                                    <IonSelectOption value="ca_student">CA Student / CA Finalist</IonSelectOption>
                                    <IonSelectOption value="other_professional">Other Professional</IonSelectOption>
                                    <IonSelectOption value="accountant_other">Accountants / Others</IonSelectOption>
                                </IonSelect>
                            </div>
                        </IonCol>

                        <IonCol size="12" sizeMd="6">
                            <div className="form-group">
                                <label className="form-label">{getDynamicLabel()}</label>
                                <input className="form-input" value={form.dynamicField} onChange={(e) => handleChange("dynamicField", e.target.value)} placeholder={getDynamicLabel()} />
                                {errors.dynamicField && <p className="danger_error_text">{errors.dynamicField}</p>}
                            </div>
                        </IonCol>
                    </IonRow>
                </IonGrid>

                <IonLoading isOpen={loading} message={"Please Wait..."} />
            </IonContent>

            <IonFooter className="job_detail_sheet_footer ion-padding">
                <IonToolbar>
                <IonRow>
                    <IonCol>
                        <IonButton className="close_job_detail_button" expand="block" onClick={onClose}>Close</IonButton>
                    </IonCol>
                    <IonCol>
                        <IonButton className="apply_job_button" onClick={handleSubmit} expand="block">Update changes</IonButton>
                    </IonCol>
                </IonRow>
                </IonToolbar>
            </IonFooter>

            {/* === IMAGE EDITOR MODAL === */}

            {(editorOpen) &&
            <AvatarCropModal
                sheet_modal_header
                imgEl={imgEl}
                rawImage={rawImage}
                editorOpen={editorOpen}
                setEditorOpen={setEditorOpen}
                doCropAndUpload={doCropAndUpload}
                frameSize={frameSize}
                offset={offset}
                setOffset={setOffset}
                zoom={zoom}
                setZoom={setZoom}
                fitMode={fitMode}
                setFitMode={setFitMode}
            />
            }

            {/* --- CUSTOM CSS (move to your stylesheet) --- */}
            <style>{`
        .profile-header.add_image_in_profile_component {
            display: flex;
            gap: 1rem;
            align-items: center;
            padding: 1rem;
            border-radius: 1rem;
            background: #ffffff;
            box-shadow: 0 6px 20px rgba(0, 0, 0, .06);
            margin: .5rem 0 1rem;
                justify-content: center;
        }
        .add_image_in_profile_component .avatar-wrap{ display:grid; align-items:center; gap:.75rem; }
        .add_image_in_profile_component .avatar-ring{  overflow:hidden;  display: flex; width:72px; height:72px; border-radius:10px;background: var(--primary-color); margin: auto;}
        .add_image_in_profile_component .avatar-img, .avatar-placeholder{
            color: #ffffff;
               margin: auto;
               overflow: hidden;
                font-weight: bold;
                font-size: var(--font-size-1);
         }
        .add_image_in_profile_component .avatar-upload-btn{ display:inline-flex; align-items:center; gap:.4rem; font-size:.9rem; cursor:pointer; padding:.4rem .6rem; border-radius:.6rem; background:#fff; border:1px solid #e6e8ff; box-shadow:0 2px 8px rgba(0,0,0,.05); }
        .add_image_in_profile_component .avatar-edit-btn{ --border-radius:12px; }
        .add_image_in_profile_component .profile-meta h2{ margin:0; font-size:1.125rem; }
        .add_image_in_profile_component .profile-meta p{ margin:.15rem 0 0; color:#5f6a86; font-size:.9rem; }

      `}</style>
        </IonModal>
    );
};

export default EditUserDetailComponent;
